import { ReactNode } from 'react';
import { Alert, Box, Collapse, Slider, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import React from 'react';

import { useSessionStore } from 'session/useSessionStore';
import { use3DGridStore } from 'features/seismic-3d/stores/use3DGridStore';
import { use3DSceneStore } from 'features/seismic-3d/stores/use3DSceneStore';
import { LineSlider } from './Lineslider';
import { use3DViewerStore } from 'features/seismic-3d/stores/use3DViewerStore';
import { VolumeType } from 'features/seismic-3d/models/enums/VolumeType';
import { AmplitudeDomain } from 'features/seismic/models/enums/AmplitudeDomain';
import { LineType } from 'features/seismic/models/enums/LineType';
import { SampleIntervalUnit } from 'features/seismic/models/enums/SampleIntervalUnity';
import { useFeatureFlags } from 'features/seismic/hooks/useFeatureFlags';
import { use3DFeatureFlags } from 'features/seismic-3d/hooks/useSeismic3DFeatureFlags';
import { ISurveyMetadata } from 'features/seismic/models/interfaces/ISurveyMetadata';

export const GridSlider = ({
    availableInlineNumbers,
    availableXlineNumbers,
    availableZSlceNumbers,
    inlineNumber,
    xlineNumber,
    zSliceNumber,
    isInlineLoading,
    isXlineLoading,
    isZSliceLoading,
    onInlineNumberChange,
    onXlineNumberChange,
    onZSliceNumberChange,
    volumeMetadata,
    isInlineVisible,
    isXlineVisible,
    isZSliceVisible,
    onInlineVisibilityChange,
    onXlineVisibilityChange,
    onZSliceVisibilityChange
} : GridSliderProps) => {
    /*const {
        volumeMetadata,
        availableInlineNumbers,
        availableXlineNumbers,
        availableZSlceNumbers,
        inlineNumber,
        xlineNumber,
        zSliceNumber,
        volumeMetadata,
        isInlineLoading,
        isXlineLoading,
        isZSliceLoading,
        onInlineNumberChange,
        onXlineNumberChange,
        onZSliceNumberChange,
        setIsInlineVisible,
        setIsXlineVisible,
        setIsZsliceVisible,
        isInlineVisible,
        isXlineVisible,
        isZsliceVisible
    } = use3DGridStore(state => ({
        volumeMetadata: state.gridSelectedSeismic,
        availableInlineNumbers: state.availableInlineNumbers,
        availableXlineNumbers: state.availableXlineNumbers,
        availableZSlceNumbers: state.availableZSlceNumbers,
        inlineNumber: state.inlineNumber,
        xlineNumber: state.xlineNumber,
        zSliceNumber: state.zSliceNumber,
        volumeMetadata: state.volumeMetadata,
        isInlineLoading: state.isInlineLoading,
        isXlineLoading: state.isXlineLoading,
        onInlineNumberChange: state.onInlineNumberChange,
        onXlineNumberChange: state.onXlineNumberChange,
        onZSliceNumberChange: state.onZSliceNumberChange,
        setIsInlineVisible: state.setIsInlineVisible,
        setIsXlineVisible: state.setIsXlineVisible,
        setIsZsliceVisible: state.setIsZsliceVisible,
        onZSliceVisibilityChange: state.onZSliceVisibilityChange,
        isZSliceLoading: state.isZSliceLoading,
        isInlineVisible: state.isInlineVisible,
        isXlineVisible: state.isXlineVisible,
        isZsliceVisible: state.isZsliceVisible,
    }), shallow);*/

    const [ volumeToken, volumeIdentifier ] = use3DViewerStore(state => [ state.mainFeatureVolumeToken, state.mainFeatureVolumeIdentifier ]);

    const tenantToken = useSessionStore(state => state.tenantConfiguration?.tenantToken);

    const volumeDomain = use3DGridStore(state => state.volumeDomain);

    const scene = use3DSceneStore(state => state.scene);

    const [ showErrorAlert, setShowErrorAlert ] = useState<boolean>(false);

    const [ gridOpacity, setGridOpacity ] = useState<number>(1);

    const lineDetailsRoute = useMemo(() =>  `${(window.location.href.includes('/app-viewer/') ? '/app-viewer' : '')}/seismic?forceDisableCache=false&${(volumeIdentifier === 'volumeId' ? 'volumeId' : 'volumeToken')}=${volumeToken}&tenantToken=${tenantToken}`, [volumeIdentifier, volumeToken, tenantToken]);

    const featureFlags = use3DFeatureFlags();

    const sampleInterval = useMemo(() => {
        if (volumeMetadata) {
            if (volumeMetadata.Header.SampleIntervalUnit === SampleIntervalUnit.Microsecond){
                return volumeMetadata.Header.SampleInterval/1000;
            } else {
                return volumeMetadata.Header.SampleInterval;
            }
        } else {
            return 0;
        }
    }, [volumeMetadata]);

    const inlineDetailsRoute = useMemo(() => `${lineDetailsRoute}&lineNumber=${inlineNumber}&lineType=${LineType.Inline}`, [lineDetailsRoute, inlineNumber]);
    const xlineDetailsRoute = useMemo(() => `${lineDetailsRoute}&lineNumber=${xlineNumber}&lineType=${LineType.Xline}`, [lineDetailsRoute, xlineNumber]);
    const zsliceDetailsRoute = useMemo(() => `${lineDetailsRoute}&lineNumber=${zSliceNumber}&lineType=${LineType.ZSlice}`, [lineDetailsRoute, zSliceNumber]);

    const onInlineSliderChange = useCallback((event: Event, value: number | number[]) => {
        const selectedInline = availableInlineNumbers[value as number];
        onInlineNumberChange(selectedInline);
    }, [onInlineNumberChange, availableInlineNumbers]);

    const onXlineSliderChange = useCallback((event: Event, value: number | number[]) => {
        const selectedXline = availableXlineNumbers[value as number];
        onXlineNumberChange(selectedXline);
    }, [onXlineNumberChange, availableXlineNumbers]);

    const onZsliceSliderChange = useCallback((event: Event, value: number | number[]) => {
        const selectedZslice = availableZSlceNumbers[value as number];
        onZSliceNumberChange(selectedZslice);
    }, [onZSliceNumberChange, availableZSlceNumbers]);

    const onInlineInputSubmit = (value: number) => {
        if (isNaN(value) || isInlineLoading) {
            return;
        }
        if (value < availableInlineNumbers[0] || value > availableInlineNumbers[availableInlineNumbers.length - 1]) {
            setShowErrorAlert(true);
        } else {
            onInlineNumberChange(value);
        }
    };

    const onXlineInputSubmit = (value: number) => {
        if (isNaN(value) || isXlineLoading) {
            return;
        }

        if (value < availableXlineNumbers[0] || value > availableXlineNumbers[availableXlineNumbers.length - 1]) {
            setShowErrorAlert(true);
        } else {
            onXlineNumberChange(value);
        }
    };

    const onZsliceInputSubmit = (value: number) => {
        if (isNaN(value) || isXlineLoading) {
            return;
        }

        if (value < availableZSlceNumbers[0] || value > availableZSlceNumbers[availableZSlceNumbers.length - 1]) {
            setShowErrorAlert(true);
        } else {
            onZSliceNumberChange(value);
        }
    };

    useEffect(() => {
        scene?.changeGridOpacity(volumeMetadata.VolumeToken, gridOpacity);
    }, [gridOpacity, scene]);

    useEffect(() => {
        if (showErrorAlert) {
            setTimeout(() => setShowErrorAlert(false), 3500);
        }
    }, [showErrorAlert]);

    return (
        <React.Fragment>
            <Box sx={{backgroundColor: 'white'}} id='seismic-3d-lines-popup-content'>
                <Collapse in={showErrorAlert}>
                    <Alert severity='error' onClose={() => setShowErrorAlert(false)}>Line out of range! Please select a line between the seismic range.</Alert>
                </Collapse>
                <Stack spacing={2}>
                    <Stack>
                        <Typography>Grid Opacity: {(gridOpacity * 100).toFixed() + '%'}</Typography>
                        <Slider
                            sx={{
                                width: '130px'
                            }}
                            id='grid-op-slider'
                            value={gridOpacity}
                            step={0.05}
                            min={0}
                            max={1}
                            onChange={(event, value) => setGridOpacity(value as number)}
                            valueLabelDisplay='off'
                        />
                    </Stack>
                    <Stack paddingRight={'10px'} spacing={2}>
                        <LineSlider
                            label='Selected Inline'
                            onLineInputSubmit={onInlineInputSubmit}
                            onSliderChange={onInlineSliderChange}
                            isSearchLoading={isInlineLoading}
                            lineIncrement={volumeMetadata.Survey3DInfo.InlineIncrement ?? 0}
                            selectedLineNumber={inlineNumber}
                            totalLineNumbers={availableInlineNumbers}
                            detailsRoute={inlineDetailsRoute}
                            onVisibilityInputChange={onInlineVisibilityChange}
                            isVisible={isInlineVisible}
                        />
                        <LineSlider
                            label='Selected Xline'
                            onLineInputSubmit={onXlineInputSubmit}
                            onSliderChange={onXlineSliderChange}
                            isSearchLoading={isXlineLoading}
                            lineIncrement={volumeMetadata.Survey3DInfo.XlineIncrement ?? 0}
                            selectedLineNumber={xlineNumber}
                            totalLineNumbers={availableXlineNumbers}
                            detailsRoute={xlineDetailsRoute}
                            onVisibilityInputChange={onXlineVisibilityChange}
                            isVisible={isXlineVisible}
                        />
                        {
                            featureFlags?.enableZslice
                            &&
                            <LineSlider
                                label={`Selected ${volumeDomain === AmplitudeDomain.Depth ? 'Depth' : 'Time'}`}
                                onLineInputSubmit={onZsliceInputSubmit}
                                onSliderChange={onZsliceSliderChange}
                                totalLineNumbers={availableZSlceNumbers}
                                onVisibilityInputChange={onZSliceVisibilityChange}
                                selectedLineNumber={zSliceNumber}
                                detailsRoute={zsliceDetailsRoute}
                                isSearchLoading={isZSliceLoading}
                                lineIncrement={sampleInterval}
                                isVisible={isZSliceVisible}
                            />
                        }
                    </Stack>
                </Stack>
            </Box>
        </React.Fragment>
    );
};

type GridSliderProps = {
    isInlineLoading: boolean,
    isXlineLoading: boolean,
    isZSliceLoading: boolean,
    zSliceNumber: number,
    xlineNumber: number,
    inlineNumber: number,
    onInlineNumberChange: (val: number) => void,
    onXlineNumberChange: (val: number) => void,
    onZSliceNumberChange: (val: number) => void,
    availableInlineNumbers: number[],
    availableXlineNumbers: number[],
    availableZSlceNumbers: number[],
    volumeMetadata: ISurveyMetadata,
    isXlineVisible: boolean,
    isZSliceVisible: boolean,
    isInlineVisible: boolean,
    onInlineVisibilityChange: (val: boolean) => void,
    onXlineVisibilityChange: (val: boolean) => void,
    onZSliceVisibilityChange: (val: boolean) => void,
};

type LineInfoTextProps = {
    children: ReactNode
};